import React from 'react';
import { BookOpen, Download, ArrowRight } from 'lucide-react';
import { trackOutboundLink } from '../utils/gtag';

const GUIDE_URL = 'https://sharonclare-hypnotherapy.kit.com/62035f1c84';
const GUIDE_TITLE = 'When The Body Learns Fear';
const GUIDE_SUBTITLE =
  'A free guide to the mind–body connection — why physical symptoms become linked with fear, how the brain learns to predict danger, and how those patterns can be unlearned.';

interface FreeGuideCTAProps {
  /** Where this CTA is rendered — used for analytics (e.g. "Home", "Stress Pillar"). */
  location: string;
  /**
   * `section` — full-width band that fits between page sections.
   * `card`    — bordered card, fits inside article/content flow (blog posts, sidebars).
   */
  variant?: 'section' | 'card';
  className?: string;
}

const FreeGuideCTA: React.FC<FreeGuideCTAProps> = ({
  location,
  variant = 'section',
  className = '',
}) => {
  const handleClick = () => {
    trackOutboundLink(GUIDE_URL, `Free Guide: ${GUIDE_TITLE} - ${location}`);
  };

  if (variant === 'card') {
    return (
      <div
        className={`bg-gradient-to-br from-primary-50 via-white to-accent-50 border-2 border-accent-200 rounded-2xl p-6 sm:p-8 ${className}`}
      >
        <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
          <div className="flex-shrink-0 w-14 h-14 bg-accent-100 rounded-2xl flex items-center justify-center">
            <BookOpen className="w-7 h-7 text-accent-600" />
          </div>
          <div className="flex-1">
            <span className="inline-block bg-accent-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-2 uppercase tracking-wide">
              Free Guide
            </span>
            <h3 className="text-xl sm:text-2xl font-heading font-bold text-gray-900 mb-2">
              {GUIDE_TITLE}
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">{GUIDE_SUBTITLE}</p>
            <a
              href={GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClick}
              className="btn-primary inline-flex items-center"
            >
              <Download className="w-5 h-5 mr-2" />
              Get the Free Guide
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className={`py-16 sm:py-20 bg-gradient-to-br from-primary-50 via-white to-accent-50 ${className}`}>
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border border-accent-100 p-8 sm:p-12">
            <div className="grid md:grid-cols-[auto_1fr] gap-8 items-center">
              <div className="flex justify-center md:justify-start">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-accent-100 to-primary-100 rounded-3xl flex items-center justify-center">
                  <BookOpen className="w-10 h-10 sm:w-12 sm:h-12 text-accent-600" />
                </div>
              </div>
              <div className="text-center md:text-left">
                <span className="inline-block bg-accent-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
                  Free Guide
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
                  {GUIDE_TITLE}
                </h2>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6">
                  {GUIDE_SUBTITLE}
                </p>
                <a
                  href={GUIDE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleClick}
                  className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 inline-flex items-center justify-center"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Get the Free Guide
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FreeGuideCTA;
